import { getGlobalSettingsAccountMailboxInfo } from 'owa-account-source-list-store';
import { type TraceErrorObject } from 'owa-analytics-types';
import { logStartGreyError } from 'owa-analytics-start';
import { setIsDataWorkerFaulted, isGulpOrBranching } from 'owa-config';
import { isDataWorkerPlatformSupported } from 'owa-data-worker-check/lib/isDataWorkerPlatformSupported';
import type { SyncEvent } from 'owa-offline-sync-diagnostics';
import { recordSyncEvents } from 'owa-offline-sync-diagnostics-client';
import { debugErrorThatWillShowErrorPopupOnly, errorThatWillCauseAlert } from 'owa-trace';
import OwaWorker from 'owa-worker-shared/lib/Worker';
import { isCriticalWorkerError } from 'owa-worker-shared/lib/utils/isCriticalWorkerError';

let worker: Worker | undefined;
let invalidWorkerError: TraceErrorObject | undefined;

const WorkerPrefix = 'DataWorker';

interface WorkerErrorEvent extends ErrorEvent {
    name: string;
    stack?: string;
}

export function loadDataWorker(): Worker | undefined {
    if (invalidWorkerError) {
        // throw a meaningful error instead of returning a broken object
        var newError = new Error('Invalid Worker Object') as TraceErrorObject;
        newError.additionalInfo = invalidWorkerError.additionalInfo;
        throw newError;
    } else if (!worker && isDataWorkerPlatformSupported()) {
        worker = new OwaWorker('owa.worker.data.js', {
            name: 'DataWorker',
        }) as Worker;

        validateWorkerObject();
        worker.onerror = onError;
    }

    return worker;
}

function validateWorkerObject() {
    if (typeof worker?.addEventListener !== 'function') {
        // seeing errors indicating this property is missing.  enumerate all properties including prototype properties
        // into a set and also get the constructor type of the object returned then log a gray error with the
        // diagnostics
        invalidWorkerError = new Error('Invalid Worker Object') as TraceErrorObject;

        // Get comprehensive property information
        const allProps = new Set<string>();
        let current = worker;
        let sanity = 10;
        const prototypeChain: string[] = [];

        while (current && sanity-- > 0) {
            prototypeChain.push(current.constructor?.name || 'Unknown');
            Object.getOwnPropertyNames(current).forEach(prop => allProps.add(prop));
            Object.getOwnPropertySymbols(current).forEach(sym => allProps.add(sym.toString()));
            current = Object.getPrototypeOf(current);
            if (current === Object.prototype) {
                break;
            }
        }

        invalidWorkerError.additionalInfo = {
            allProperties: Array.from(allProps).sort(),
            prototypeChain,
            constructor: worker?.constructor?.name,
            hasPostMessage: typeof worker?.postMessage,
            hasTerminate: typeof worker?.terminate,
            hasOnMessage: typeof worker?.onmessage,
            hasAddEventListener: typeof worker?.addEventListener,
            objectType: Object.prototype.toString.call(worker),
            isInstanceOfWorker: worker instanceof Worker,
        };
        logStartGreyError(
            /* eslint-disable-next-line owa-custom-rules/telemetry-event-name-format -- (https://aka.ms/OWALintWiki)
             * Baseline. DO NOT COPY AND PASTE! Grandfathering existing event names; Devs who touch this file should update Event Names to follow Outlook Telemetry Naming Standards
             *	> Invalid Outlook telemetry EventName "worker_invalid_object": must start with a capital letter (A–Z). */
            'worker_invalid_object',
            getGlobalSettingsAccountMailboxInfo(),
            invalidWorkerError
        );
        throw invalidWorkerError;
    }
}

function onError(ev: ErrorEvent) {
    if (isCriticalWorkerError(ev)) {
        setIsDataWorkerFaulted(ev as any);
        /* eslint-disable-next-line owa-custom-rules/telemetry-event-name-format -- (https://aka.ms/OWALintWiki)
         * Baseline. DO NOT COPY AND PASTE! Grandfathering existing event names; Devs who touch this file should update Event Names to follow Outlook Telemetry Naming Standards
         *	> Invalid Outlook telemetry EventName "worker_init_error": must start with a capital letter (A–Z). */
        logStartGreyError('worker_init_error', getGlobalSettingsAccountMailboxInfo(), ev.error);
        const syncEvent: SyncEvent = {
            type: 'EngineStateChanged',
            data: ['Worker Fault: ' + ev.message],
        };
        recordSyncEvents([syncEvent]);

        // popup error for devs and branch users to make worker breaks more obvious
        // there can be hash at the end of the script name
        if (isGulpOrBranching() && ev.message?.includes('owa.worker.data.')) {
            debugErrorThatWillShowErrorPopupOnly(
                'DataWorker entry script failed to execute. Offline features will not be available.  Check console errors for more details.'
            );
        }
    } else {
        const workerError = ev as WorkerErrorEvent;
        workerError.name = WorkerPrefix;
        let message = workerError.message;
        const newlineIndex = message?.indexOf('\n');
        if (newlineIndex > 0) {
            workerError.stack = message.substring(newlineIndex + 1);
            message = message.substring(0, newlineIndex);
        }
        /* eslint-disable-next-line owa-custom-rules/no-error-dynamic-event-names -- (https://aka.ms/OWALintWiki)
         * The error name (message) must be a string literal (no variables in it).
         *	> Error names can only be a string literals. Use the diagnosticInfo to add custom data. */
        errorThatWillCauseAlert(WorkerPrefix + ':' + message, workerError.error ?? workerError);
    }
}

export function testReset(): void {
    invalidWorkerError = undefined;
    worker = undefined;
}
